import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { useProgram } from 'shared/providers/ProgramProvider';
import { ServiceGroup } from '../ImpactIndicator/utils';
import Selector, {
  SelectorOptions,
} from 'shared/design-system/components/Selector';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { faUser } from '@fortawesome/pro-light-svg-icons';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { OtherTextField } from './SectionService';
import { getDefaultPrimaryBeneficiary } from './defaultPrimaryBeneficiary';
import { GuidanceTextResponse } from '../utils';
import { useIndicator } from 'shared/providers/IndicatorProvider';

type ServiceGroupProps =
  | ServiceGroup[]
  | ServiceGroup['services']
  | { dbId: number; name: string }[];

export interface PrimaryBeneficiaryProps {
  guidanceText: GuidanceTextResponse;
  data: ServiceGroupProps;
  value?: string;
  questionId: number;
  answers: Answer[];
  mainBeneficiaryText?: string;
  onChange: (
    questionId: number,
    value: string,
    forcedValue?: number | null,
  ) => unknown;
}

const StyledDiv = styled.div`
  .select-main-beneficiary {
    width: 244px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${SPACERS.large};
  }
  .sub-heading {
    max-width: 450px;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }
  .text-field {
    margin-top: ${SPACERS.small};
    margin-bottom: ${SPACERS.large};
  }
  .show-more {
    cursor: pointer;
    text-align: center;
    margin-bottom: ${SPACERS.large};
  }
  .divider {
    width: 241px;
    height: 1px;
    margin-left: auto;
    margin-right: auto;
    margin-top: ${SPACERS.large};
    margin-bottom: ${SPACERS.large};
    background-color: ${COLORS.grayBase};
  }
  .main-beneficiary-wrapper {
    width: 450px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const getOtherText = (
  options: string[],
  selected: string | undefined,
  mainBeneficiaryText: string | undefined,
) => {
  if (!selected || selected === mainBeneficiaryText) return undefined;
  return options.findIndex(x => x === selected) === -1 ? selected : undefined;
};
const defaultGetLabel = (value: string) => value;
const PrimaryBeneficiary: React.FC<PrimaryBeneficiaryProps & {
  isProgramLevelQuestion?: boolean;
}> = ({
  data,
  questionId,
  answers,
  mainBeneficiaryText,
  value,
  onChange,
  isProgramLevelQuestion,
}) => {
  const { setBeneficiaryName } = useIndicator();
  const defaultBeneficiary = useMemo(
    () =>
      getDefaultPrimaryBeneficiary(
        answers.find(x => x.questionId === 40003)?.text,
      ),
    [answers],
  );
  mainBeneficiaryText = defaultBeneficiary;
  const options = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = data?.map((row: { name: any }) => row.name);
    if (result[result.length - 1] === 'Other') return result.slice(0, -1);
    return result;
  }, [data]);
  const [selected, setSelected] = useState<string | undefined>(value);
  const [other, setOther] = useState<string | undefined>(
    getOtherText(options, selected, mainBeneficiaryText),
  );

  // One-shot auto-populate from the program-level answer when the report
  // has no answer yet. Field stays editable.
  const { answers: programAnswers } = useProgram();
  const programAnswerText = programAnswers?.[questionId]?.text;
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (!isProgramLevelQuestion) return;
    if (!programAnswerText) return;
    if (value) {
      seededRef.current = true;
      return;
    }
    seededRef.current = true;
    setSelected(programAnswerText);
    setBeneficiaryName(programAnswerText);
    onChange(questionId, programAnswerText);
  }, [
    isProgramLevelQuestion,
    programAnswerText,
    value,
    questionId,
    onChange,
    setBeneficiaryName,
  ]);

  useEffect(() => {
    if (selected) {
      setBeneficiaryName(selected);
      onChange(questionId, selected);
      return;
    }
    // const result =
    //   defaultBeneficiary ||
    //   getDefaultPrimaryBeneficiary(
    //     answers.find(x => x.questionId === 40003)?.text,
    //   );
    // if (!result) return;
    // setSelected(result);
    // setOther(getOtherText(options, result, mainBeneficiaryText));
    // setBeneficiaryName(result);
    // onChange(questionId, result);
  }, [answers]);

  const handleWrite = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setOther(value);
    },
    [setOther],
  );

  const handleBlur = useCallback(() => {
    setSelected(other);
    other && onChange(questionId, other);
  }, [other, questionId, onChange, setSelected]);

  const handleBeneficiary = useCallback(
    (v: string) => {
      setBeneficiaryName(v);
      onChange(questionId, v);
      if (v) setSelected(v);
      setOther(undefined);
    },
    [questionId, onChange, setOther, mainBeneficiaryText],
  );

  const handleChange = useCallback(() => {
    if (!mainBeneficiaryText) return;
    const newValue = value === mainBeneficiaryText ? '' : mainBeneficiaryText;
    setBeneficiaryName(newValue);
    onChange(questionId, newValue);
    setOther(undefined);
    setSelected(undefined);
  }, [value, mainBeneficiaryText, questionId, onChange, setOther]);

  const handleOtherClick = useCallback(() => {
    setOther(o => (o === undefined ? '' : undefined));
    setSelected('');
  }, [setOther, onChange]);

  return (
    <StyledDiv>
      {mainBeneficiaryText && (
        <Selector
          variant="primary-checkbox"
          icon={faUser}
          selected={value === mainBeneficiaryText}
          onClick={handleChange}
          label={mainBeneficiaryText}
          className="select-main-beneficiary"
        />
      )}
      {mainBeneficiaryText && (
        <TrueImpactText className="sub-heading">
          If you use a different term for your end beneficiaries or if another
          term better describes them, select it below.
        </TrueImpactText>
      )}
      <div className="divider" />
      <div className="main-beneficiary-wrapper">
        <SelectorOptions
          variant="radio"
          padding={SPACERS.tiny}
          options={options}
          getLabel={defaultGetLabel}
          getSelected={val => val === selected}
          onClick={handleBeneficiary}
          className="beneficiary-options"
        />
        {/* 
        Code for adding other option
        <Selector
          variant="radio"
          label="Other"
          selected={other !== undefined}
          padding={SPACERS.tiny}
          margin={`${SPACERS.tiny} auto`}
          onClick={handleOtherClick}
        />
        {other !== undefined && (
          <OtherTextField
            value={other || ''}
            placeholder="Please specify"
            onChange={handleWrite}
            onBlur={handleBlur}
            autoFocus={true}
          />
        )} */}
      </div>
    </StyledDiv>
  );
};

export default PrimaryBeneficiary;
